import { format } from "date-fns";
import {
  AlertCircle,
  BarChart3,
  Bot,
  CreditCard,
  Crown,
  Download,
  Feather,
  Filter,
  Info,
  Loader2,
  Receipt,
  Shield,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { getEnterpriseCoverageStatus, type EnterpriseCoverageStatus } from "@/app/_module/services/api/agency-billing.api";

import { usePurchaseHistory, usePurchaseStats } from "../hooks/queries";
import { transactionService } from "../services/transactionService";
import type { PurchaseHistoryItem } from "../types/transactions";
import { formatPhoneNumberDisplay } from "@/app/_module/utils/phoneUtils";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";

const tableHeaders: readonly string[] = [
  "Invoice ID",
  "Date",
  "Description",
  "Payment Method",
  "Amount",
  "Status",
  "Next Billing",
  "Actions"
] as const;

// Card brand icon component with official brand logos
const CardBrandIcon: React.FC<{ paymentMethod: string }> = ({ paymentMethod }) => {
  const getBrandIcon = () => {
    const methodLower = paymentMethod.toLowerCase();

    if (methodLower.includes('visa')) {
      return (
        <svg className="h-6 w-auto" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M780 0H0V500H780V0Z" fill="#1434CB"/>
          <path d="M489.823 143.111C442.988 143.111 401.134 167.393 401.134 212.256C401.134 263.706 475.364 267.259 475.364 293.106C475.364 303.989 462.895 313.731 441.6 313.731C411.377 313.731 388.789 300.119 388.789 300.119L379.123 345.391C379.123 345.391 405.145 356.889 439.692 356.889C490.898 356.889 531.19 331.415 531.19 285.784C531.19 231.419 456.652 227.971 456.652 203.981C456.652 195.455 466.887 186.114 488.122 186.114C512.081 186.114 531.628 196.014 531.628 196.014L541.087 152.289C541.087 152.289 519.818 143.111 489.823 143.111ZM61.3294 146.411L60.1953 153.011C60.1953 153.011 79.8988 156.618 97.645 163.814C120.495 172.064 122.122 176.868 125.971 191.786L167.905 353.486H224.118L310.719 146.411H254.635L198.989 287.202L176.282 167.861C174.199 154.203 163.651 146.411 150.74 146.411H61.3294ZM333.271 146.411L289.275 353.486H342.756L386.598 146.411H333.271ZM631.554 146.411C618.658 146.411 611.825 153.318 606.811 165.386L528.458 353.486H584.542L595.393 322.136H663.72L670.318 353.486H719.805L676.633 146.411H631.554ZM638.848 202.356L655.473 280.061H610.935L638.848 202.356Z" fill="white"/>
        </svg>
      );
    } else if (methodLower.includes('mastercard')) {
      return (
        <svg className="h-6 w-auto" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M780 0H0V500H780V0Z" fill="#253747"/>
          <path d="M465.738 69.1387H313.812V342.088H465.738V69.1387Z" fill="#FF5A00"/>
          <path d="M323.926 205.613C323.926 150.158 349.996 100.94 390 69.1387C360.559 45.9902 323.42 32 282.91 32C186.945 32 109.297 109.648 109.297 205.613C109.297 301.578 186.945 379.227 282.91 379.227C323.42 379.227 360.559 365.237 390 342.088C349.94 310.737 323.926 261.069 323.926 205.613Z" fill="#EB001B"/>
          <path d="M670.711 205.613C670.711 301.578 593.062 379.227 497.098 379.227C456.588 379.227 419.449 365.237 390.008 342.088C430.518 310.231 456.082 261.069 456.082 205.613C456.082 150.158 430.012 100.94 390.008 69.1387C419.393 45.9902 456.532 32 497.041 32C593.062 32 670.711 110.154 670.711 205.613Z" fill="#F79E1B"/>
        </svg>
      );
    } else if (methodLower.includes('amex') || methodLower.includes('american express')) {
      return (
        <svg className="h-6 w-auto" viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
          <rect width="780" height="500" fill="#2557D6"/>
          <path d="M0.253 235.69h37.441l8.442-19.51h18.9l8.42 19.51h73.668v-14.915l6.576 14.98h38.243l6.576-15.202v15.138h183.08l-0.085-32.026h3.542c2.479 0.083 3.204 0.302 3.204 4.226v27.8h94.689v-7.455c7.639 3.92 19.518 7.455 35.148 7.455h39.836l8.525-19.51h18.9l8.337 19.51h76.765v-18.532l11.626 18.532h61.515v-122.51h-60.88v14.468l-8.522-14.468h-62.471v14.468l-7.828-14.468h-84.38c-14.123 0-26.539 1.889-36.569 7.153v-7.153h-58.229v7.153c-6.383-5.426-15.079-7.153-24.75-7.153h-212.74l-14.274 31.641-14.659-31.641h-67.005v14.468l-7.362-14.468h-57.145l-26.539 58.246v64.261h3e-3zm236.34-17.67h-22.464l-0.083-68.794-31.775 68.793h-19.24l-31.858-68.854v68.854h-44.57l-8.42-19.592h-45.627l-8.505 19.592h-23.801l39.241-87.837h32.559l37.269 83.164v-83.164h35.766l28.678 59.587 26.344-59.587h36.485l1e-3 87.838zm-165.9-37.823l-14.998-35.017-14.915 35.017h29.913zm255.3 37.821h-73.203v-87.837h73.203v18.291h-51.289v15.833h50.06v18.005h-50.061v17.542h51.289l1e-3 18.166zm103.16-64.18c0 14.004-9.755 21.24-15.439 23.412 4.794 1.748 8.891 4.838 10.84 7.397 3.094 4.369 3.628 8.271 3.628 16.116v17.255h-22.104l-0.083-11.077c0-5.285 0.528-12.886-3.458-17.112-3.202-3.09-8.083-3.76-15.973-3.76h-23.523v31.95h-21.914v-87.838h50.401c11.199 0 19.451 0.283 26.535 4.207 6.933 3.924 11.09 9.652 11.09 19.45zm-27.699 13.042c-3.013 1.752-6.573 1.81-10.841 1.81h-26.62v-19.51h26.982c3.818 0 7.804 0.164 10.393 1.584 2.842 1.28 4.601 4.003 4.601 7.765 0 3.84-1.674 6.929-4.515 8.351z" fill="#fff"/>
        </svg>
      );
    } else if (methodLower.includes('discover')) {
      return (
        <svg className="h-6 w-auto" viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
          <path d="M54.992 0C0 0 0 0 0 0v0C0 0 0 0 0 501h670.016C755.373 501 780 476.37 780 445.996V0C0 0 755.381 0 725.008 0H54.992z" fill="#4D4D4D"/>
          <path d="M327.152 161.893c8.837 0 16.248 1.784 25.268 6.09v22.751c-8.544-7.863-15.955-11.154-25.756-11.154-19.264 0-34.414 15.015-34.414 34.05 0 20.075 14.681 34.196 35.37 34.196 9.312 0 16.586-3.12 24.8-10.857v22.763c-9.341 4.14-16.911 5.776-25.756 5.776-31.278 0-55.582-22.596-55.582-51.737 0-28.826 24.951-51.878 56.07-51.878z" fill="#FFF"/>
          <path d="M779.983 288.36c-26.05 18.33-221.077 149.34-558.754 212.623H724.99c30.365 0 54.992-0 54.992-0V0z" fill="#F47216"/>
        </svg>
      );
    } else if (methodLower.includes('diners')) {
      return (
        <svg className="h-6 w-auto" viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
          <rect width="780" height="500" fill="#0079BE"/>
          <path d="M599.93 251.45c0-99.415-82.98-168.13-173.9-168.1h-78.242c-92.003-0.033-167.73 68.705-167.73 168.1 0 90.93 75.727 165.64 167.73 165.2h78.242c90.914 0.436 173.9-74.294 173.9-165.2z" fill="#fff"/>
          <path d="M348.28 97.43c-84.07 0.027-152.19 68.308-152.21 152.58 0.02 84.258 68.144 152.53 152.21 152.56 84.09-0.027 152.23-68.303 152.24-152.56-0.011-84.272-68.149-152.55-152.24-152.58z" fill="#0079BE"/>
          <path d="M252.07 249.6c0.08-41.181 25.746-76.297 61.94-90.25v180.48c-36.194-13.948-61.861-49.045-61.94-90.23zm131 90.274v-180.53c36.207 13.92 61.914 49.057 61.979 90.257-0.065 41.212-25.772 76.322-61.979 90.269z" fill="#fff"/>
        </svg>
      );
    }

    // Default generic credit card icon
    return <CreditCard className="h-5 w-5 text-gray-500" />;
  };

  return <div className="flex-shrink-0">{getBrandIcon()}</div>;
};

export const PurchaseHistory: React.FC = function () {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "all": return "All Statuses";
      case "COMPLETED": return "Completed";
      case "PENDING": return "Pending";
      case "FAILED": return "Failed";
      case "CANCELLED": return "Cancelled";
      case "REFUNDED": return "Refunded";
      default: return "Filter by status";
    }
  };

  const getPlanIcon = (planName: string | undefined) => {
    if (!planName) return <CreditCard className="h-6 w-6 text-gray-500" />;

    const planLower = planName.toLowerCase();

    // Match the exact logic from SubscriptionPlans.tsx (lines 856-868)
    if (planLower.includes('premium') || planLower.includes('full_fledged') || planLower.includes('full-fledged')) {
      return <Crown className="h-6 w-6 text-yellow-500" />;
    } else if (planLower.includes('professional')) {
      return <Zap className="h-6 w-6 text-blue-500" />;
    } else if (planLower.includes('starter') || planLower.includes('basic')) {
      return <BarChart3 className="h-6 w-6 text-gray-500" />;
    } else if (planLower.includes('super')) {
      return <Bot className="h-6 w-6 text-amber-500" />;
    } else if (planLower.includes('enterprise')) {
      return <Shield className="h-6 w-6 text-purple-500" />;
    } else if (planLower.includes('lite')) {
      return <Feather className="h-6 w-6 text-slate-500" />;
    }

    // Default to a neutral icon for unknown plans
    return <CreditCard className="h-6 w-6 text-gray-500" />;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: purchaseHistoryData,
    isLoading: purchaseHistoryLoading,
    error: purchaseHistoryError,
    refetch: refetchPurchaseHistory,
  } = usePurchaseHistory({
    page: currentPage,
    offset: 10,
    search: debouncedSearch || undefined,
    status: statusFilter === "all" ? undefined : statusFilter,
  });

  const {
    data: purchaseStats,
    isLoading: statsLoading,
    error: statsError,
  } = usePurchaseStats();

  // Check enterprise status to override plan display for enterprise owners
  const { data: enterpriseStatus } = useQuery<EnterpriseCoverageStatus>({
    queryKey: ['enterprise-coverage-status'],
    queryFn: getEnterpriseCoverageStatus,
    staleTime: 1000 * 60 * 5,
  });
  const isEnterpriseOwner = enterpriseStatus?.isOwner === true;
  const isEnterpriseCovered = enterpriseStatus?.isCovered === true && !isEnterpriseOwner;

  const purchases = purchaseHistoryData?.items || [];
  const totalPages: number = purchaseHistoryData?.totalPages || 1;
  const totalItems: number = purchaseHistoryData?.totalItems || 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "refunded":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleDownloadInvoice = (
    invoiceUrl: string | undefined,
    invoiceId: string
  ) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank");
    }
  };

  // Native confirm() is a browser chrome dialog — it says "app.insuracentral.com
  // says", cannot be styled, and looks nothing like the rest of the product.
  // Hold the pending target in state and confirm in our own dialog instead.
  const [cancelTarget, setCancelTarget] = useState<PurchaseHistoryItem | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancelTransaction = (purchase: PurchaseHistoryItem) => {
    setCancelTarget(purchase);
  };

  const confirmCancelTransaction = async () => {
    if (!cancelTarget?.id) return;
    setIsCancelling(true);
    try {
      await transactionService.cancelTransaction(cancelTarget.id);
      refetchPurchaseHistory();
      setCancelTarget(null);
    } catch (error) {
      // Error handling is done in the service
    } finally {
      setIsCancelling(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  // Handle errors
  if (purchaseHistoryError || statsError) {
    const errorMessage = 
      (purchaseHistoryError as Error)?.message || 
      (statsError as Error)?.message || 
      "An error occurred";
    return (
      <Alert className="mx-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              <div className="w-full">
                <p className="text-xs text-muted-foreground mb-1">Total Spent</p>
                {statsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  <p className="text-xl font-bold truncate">
                    {purchaseStats
                      ? formatCurrency(
                          purchaseStats.totalSpent,
                          purchaseStats.currency
                        )
                      : "$0.00"}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              <div className="w-full">
                <p className="text-xs text-muted-foreground mb-1">Total Orders</p>
                {statsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  <p className="text-xl font-bold">
                    {purchaseStats?.totalOrders || 0}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center gap-2">
              {statsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                getPlanIcon(
                  isEnterpriseOwner ? 'enterprise' :
                  isEnterpriseCovered ? 'enterprise' :
                  purchaseStats?.currentPlan
                )
              )}
              <div className="w-full">
                <p className="text-xs text-muted-foreground mb-1">Current Plan</p>
                {statsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  <>
                    <p className="text-xl font-bold truncate">
                      {isEnterpriseOwner ? "Enterprise" :
                       isEnterpriseCovered ? (purchaseStats?.currentPlan || "Covered") :
                       (purchaseStats?.currentPlan || "None")}
                    </p>
                    {isEnterpriseCovered ? (
                      <p className="text-xs text-green-600 mt-1 truncate">
                        Covered by {enterpriseStatus?.enterpriseAgencyName || 'organization'}
                      </p>
                    ) : !isEnterpriseOwner && !!purchaseStats?.nextBillingDate ? (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {purchaseStats?.autoRenew === false
                          ? `Subscription ends: ${purchaseStats.nextBillingDate}`
                          : `Next billing: ${purchaseStats.nextBillingDate}`}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Purchase History
              </CardTitle>
              <CardDescription>
                View and download your transaction history
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1); // Reset to first page when filter changes
              }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Filter className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-10 w-64"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search purchases..."
                  value={searchTerm}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {purchaseHistoryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">Loading purchase history...</span>
            </div>
          ) : (
            <React.Fragment>
              <div className="border rounded-lg overflow-x-auto scrollbar-hide">
                <Table className="min-w-[800px]">
                  <TableHeader>
                    <TableRow>
                      {tableHeaders.map((header, index) => (
                        <TableHead key={`header-${index}`}>{header}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchases.map((purchase: PurchaseHistoryItem) => (
                      <TableRow key={purchase.invoiceId}>
                        <TableCell className="font-medium">
                          {purchase.invoiceId}
                        </TableCell>
                        <TableCell>{formatDate(purchase.date)}</TableCell>
                        <TableCell>
                          <div>{purchase.description}</div>
                          {/* Spell out the substitution. The invoice shows the code
                              ORDERED, the number begins with the code RECEIVED, and
                              without this line that reads as a billing error — which
                              is exactly the confusion agents reported. */}
                          {purchase.provisionedNumbers
                            ?.filter((p) => p.substituted)
                            .map((p) => (
                              <div
                                key={p.areaCode}
                                className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
                              >
                                <span>
                                  Area code {p.areaCode} → provisioned{' '}
                                  <span className="font-mono">
                                    {p.numbers.map(formatPhoneNumberDisplay).join(', ')}
                                  </span>
                                </span>
                                <TooltipProvider>
                                  <UITooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        aria-label="Why is this number a different area code?"
                                        className="inline-flex cursor-help items-center align-middle"
                                      >
                                        <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                                      </button>
                                    </TooltipTrigger>
                                    {/* Explains the swap WITHOUT naming the carrier —
                                        vendor names never appear in agent-facing copy. */}
                                    <TooltipContent
                                      side="top"
                                      className="max-w-72 text-left text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground"
                                    >
                                      Area code {p.areaCode} was out of stock when this order
                                      was filled, so the closest available number in the same
                                      local area was provisioned instead. Same price, same
                                      coverage — you were billed for {p.areaCode}.
                                    </TooltipContent>
                                  </UITooltip>
                                </TooltipProvider>
                              </div>
                            ))}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {purchase.paymentMethod && (
                              <CardBrandIcon paymentMethod={purchase.paymentMethod} />
                            )}
                            <span>{purchase.paymentMethod || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatCurrency(purchase.amount, purchase.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(purchase.status)}>
                            {purchase.status.charAt(0).toUpperCase() +
                              purchase.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {purchase.nextBillingDate ? (
                            <span className="text-sm text-muted-foreground">
                              {purchase.nextBillingDate}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {purchase.status === "completed" && (
                              <Button
                                className="flex items-center gap-1"
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  handleDownloadInvoice(
                                    purchase.invoiceUrl,
                                    purchase.invoiceId
                                  )
                                }
                              >
                                <Download className="h-4 w-4" />
                                Invoice
                              </Button>
                            )}
                            {purchase.status === "pending" && purchase.id && (
                              <Button
                                className="flex items-center gap-1"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancelTransaction(purchase)}
                              >
                                <X className="h-4 w-4" />
                                Cancel
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {purchases.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No purchases found matching your search.</p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {purchases.length} of {totalItems} results
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      disabled={currentPage === 1}
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setCurrentPage((prev: number) => Math.max(1, prev - 1))
                      }
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      disabled={currentPage === totalPages}
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setCurrentPage((prev: number) => Math.min(totalPages, prev + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </React.Fragment>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Cancel this purchase?
            </DialogTitle>
            <DialogDescription>
              This checkout was started but never paid. Cancelling it releases the
              hold so you can buy other area codes straight away.
            </DialogDescription>
          </DialogHeader>

          {cancelTarget && (
            <div className="bg-muted/40 space-y-1 rounded-lg border p-3 text-sm">
              <div className="font-medium">{cancelTarget.description}</div>
              <div className="text-muted-foreground text-xs">
                {cancelTarget.invoiceId} &middot; {formatDate(cancelTarget.date)} &middot;{" "}
                {formatCurrency(cancelTarget.amount, cancelTarget.currency)}
              </div>
            </div>
          )}

          <p className="text-muted-foreground text-xs">
            Nothing has been charged, and nothing will be. This cannot be undone —
            you would need to start a new purchase.
          </p>

          <div className="flex justify-end gap-3 pt-1">
            <Button
              variant="outline"
              onClick={() => setCancelTarget(null)}
              disabled={isCancelling}
            >
              Keep it
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancelTransaction}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling…" : "Cancel purchase"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
